
import { computed, defineComponent, Ref, ref, onUnmounted, watch } from 'vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import wishListsStore from '../wish-lists.store';
import pageStore from '@/shared/content/page.store';
import AddToBasket from '@/b2c/shared/checkout/ui/AddToBasket.vue';
import WishListItem from '@/b2c/wishlists/ui/WishListItem.vue';
import WishListDeleteButton from '@/b2c/wishlists/ui/WishListDeleteButton.vue';
import WishListShareButton from '@/b2c/wishlists/ui/WishListShareButton.vue';
import WishListRename from '@/b2c/wishlists/ui/WishListRename.vue';
import NativeShareButton from '@/shared/NativeShareButton.vue';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { WishList } from '@/b2c/shared/api/server-apis';
import RouterLinkConditional from '@/core/navigation/router-link-conditional.vue';
import siteContextService from '@/b2c/shared/site-context.service';
import { LinkModel } from '@/types/B2CcontentServerContract';
import basketTrackingService from '@/shared/tracking/basket-tracking.service';

export default defineComponent({
    name: 'WishList',
    components: { AddToBasket, RouterLinkConditional, NativeShareButton, WishListRename, WishListShareButton, WishListDeleteButton, WishListItem },
    props: {
        sharePath: {
            type: String,
            required: false,
            default: '',
        },
    },
    setup(props) {
        const labels = useBlockLabels();
        const showDefaultWishListShare: Ref<boolean> = ref(false);
        const titleIsBeingRenamed: Ref<boolean> = ref(false);
        const id = useRouteQuery<string>(QueryKeys.Id);

        const shareUrl = computed(() => {
            return wishListsStore.getFormattedShareUrl(props.sharePath);
        });

        const listDefaultImageUrl: Ref<string | undefined> = computed(() => {
            return wishListsStore.getDetailsShareImageUrl(details.value);
        });

        const details: Ref<WishList> = computed(() => {
            return wishListsStore.details;
        });

        const loadingDetails: Ref<boolean> = computed(() => {
            return wishListsStore.pending;
        });

        const wishListItems = computed(() => {
            return details.value?.wishListItems || [];
        });

        const wishListItemsForAddToBasket = computed(() => {
            const availableSku = details.value.purchasableProducts || [];
            return wishListItems.value.filter(item => availableSku.includes(item.variant.sku)).map(item => ({ sku: item.variant.sku, purchaseOptions: item.purchaseOptions, variantId: item.variant.itemNumber }));
        });

        const hasWishListItems = computed(() => {
            return !loadingDetails.value && details.value?.wishListItems.length > 0;
        });

        const areAllWishListItemReadyForPurchase = computed(() => {
            return (wishListItemsForAddToBasket.value || []).length && !siteContextService.isBrandSite;
        });

        const toggleWishlistRenameMode = (): void => {
            titleIsBeingRenamed.value = !titleIsBeingRenamed.value;
        };

        const updatePageAltTitle = (): void => {
            pageStore.setAltTitle(wishListsStore.details.name);
        };

        const updateDetails = (): void => {
            wishListsStore.updateDetails(id.value).then(updatePageAltTitle);
        };

        const findStoresLink: Ref<LinkModel> = computed(() => {
            return siteContextService.findStoresLink ?? {} as LinkModel;
        });

        const isPurchaseAble = (sku: string): boolean => {
            return (details.value.purchasableProducts || []).includes(sku);
        };

        const trackAddListToBasket = () => {
            basketTrackingService.wishlistAll(wishListsStore.details);
        };

        watch(() => id, (newQuery) => {
            if (id.value) {
                updateDetails();
            }
        }, { immediate: true });

        onUnmounted(() => {
            wishListsStore.clearDetails();
            pageStore.clearAltTitle();
        });

        return {
            labels,
            details,
            loadingDetails,
            shareUrl,
            hasWishListItems,
            wishListItemsForAddToBasket,
            titleIsBeingRenamed,
            showDefaultWishListShare,
            areAllWishListItemReadyForPurchase,
            isBrandSite: computed(() => siteContextService.isBrandSite),
            findStoresLink,
            toggleWishlistRenameMode,
            listDefaultImageUrl,
            pageStore,
            trackAddListToBasket,
            isPurchaseAble,
        };
    },
});
